import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard } from 'lucide-react';
import { useAuth } from "./AuthContext";
import AuthEntryButtons from "./AuthEntryButtons";
import { isAppHost, shouldEnforceDomainSplit, toAppUrl } from "../constants/siteDomains";
import { MARKETING_HOME_URL } from "../constants/externalLinks";
import { clearSessionMarker, hasCrossDomainSessionHint } from "../utils/sessionMarker";

type AuthNavButtonProps = {
    isMobile?: boolean;
    /** Barra superior no celular: só ícone de sair, sem empilhar Painel + Sair. */
    compact?: boolean;
};

const AuthNavButton = ({ isMobile = false, compact = false }: AuthNavButtonProps) => {
    const { isAuthenticated, isLoading, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const painelUrl = toAppUrl('/painel');
    const logoutUrl = toAppUrl('/sair');
    const showLoggedInNav = hasCrossDomainSessionHint(isAuthenticated);

    const handleLogout = async () => {
        clearSessionMarker();

        if (isAuthenticated) {
            await logout();
            if (shouldEnforceDomainSplit() && !isAppHost()) {
                window.location.assign(MARKETING_HOME_URL);
                return;
            }
            navigate('/login');
            return;
        }

        window.location.assign(logoutUrl);
    };

    if (isLoading) {
        return (
            <span className="text-sm text-[#3A5859]/60" aria-live="polite">
                ...
            </span>
        );
    }

    if (showLoggedInNav) {
        const onPainel = location.pathname.startsWith('/painel');

        if (compact) {
            return (
                <div className="flex items-center gap-2">
                    {!onPainel && (
                        <a
                            href={painelUrl}
                            title="Painel"
                            className="flex items-center justify-center rounded-full bg-[#A56096] p-2 text-white hover:bg-[#8B4A7D]"
                        >
                            <LayoutDashboard className="h-5 w-5" />
                        </a>
                    )}
                    <button
                        type="button"
                        onClick={() => { void handleLogout(); }}
                        title="Sair"
                        className="flex items-center justify-center rounded-full bg-red-100 p-2 text-red-600 hover:bg-red-200"
                    >
                        <LogOut className="h-5 w-5" />
                    </button>
                </div>
            );
        }

        return (
            <div className={`flex items-center ${isMobile ? 'flex-col gap-3 w-full' : 'gap-3'}`}>
                <a
                    href={painelUrl}
                    className={`flex items-center justify-center gap-2 font-medium transition-colors ${isMobile
                            ? 'w-full rounded-full bg-[#A56096] px-4 py-3 text-sm text-white'
                            : 'text-[#3A5859] hover:text-[#A56096]'
                        }`}
                >
                    <LayoutDashboard className="h-4 w-4" />
                    Painel
                </a>

                <button
                    type="button"
                    onClick={() => { void handleLogout(); }}
                    title="Sair"
                    className={`flex items-center justify-center gap-2 rounded-full font-medium transition-colors ${isMobile
                            ? 'w-full bg-red-500 px-4 py-3 text-sm text-white hover:bg-red-600'
                            : 'bg-red-100 px-3 py-2 text-sm text-red-600 hover:bg-red-200'
                        }`}
                >
                    <LogOut className="h-4 w-4" />
                    Sair
                </button>
            </div>
        );
    }

    return <AuthEntryButtons variant="header" />;
};

export default AuthNavButton;
